import React, { useState, useCallback, useEffect } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from 'emailjs-com';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import { extendedSchema } from '../schema';
import { Modal } from 'react-bootstrap';
import CustomModal from './CustomModal';
import './CustomForm.css';

const CustomForm = ({
  firstNameLabel,
  lastNameLabel,
  emailLabel,
  phoneLabel,
  typeLabel,
  messageLabel,
  ageLabel,
  submitLabel,
  formType
}) => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [captchaExpired, setCaptchaExpired] = useState(false);
  const [captchaCompleted, setCaptchaCompleted] = useState(false);
  const [captchaSize, setCaptchaSize] = useState(
    window.innerWidth < 360 ? "compact" : "normal"
  );
  const [validationSchema, setValidationSchema] = useState(() =>
    extendedSchema(formType, i18n.language)
  );

  useEffect(() => {
    setValidationSchema(extendedSchema(formType, i18n.language));
  }, [formType, i18n.language]);

  useEffect(() => {
    const handleResize = () => {
      setCaptchaSize(window.innerWidth < 360 ? "compact" : "normal");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const onChange = useCallback(() => {
    setCaptchaExpired(false);
    setCaptchaCompleted(true);
  }, []);

  const setRecaptchaCookie = () => {
    document.cookie = "_GRECAPTCHA=value; Secure; HttpOnly; SameSite=None; Partitioned";
  };

  useEffect(() => {
    setRecaptchaCookie(); // Call the function when the component mounts
  }, []); // Ensure this effect runs only once when the component mounts

  const onSubmit = async (values, actions, event) => {
    try {
      // Prevent form submission if event is defined
      event?.preventDefault();

      const errors = await actions.validateForm();

      if (Object.keys(errors).length > 0) {
        actions.setTouched(
          Object.keys(errors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
          false 
        );
        actions.setErrors(errors);
        return;
      }

      const isFormValid = await actions.validateForm();

      if (!isFormValid || !captchaCompleted) {
        if (!captchaCompleted) {
          setTitle(t('form.title-error-1'));
          setMessage(t('form.error-1'));
          setShowModal(true);
        }
        return;
      }
      // Check if reCAPTCHA has expired
      if (captchaExpired) {
        setTitle(t('form.title-error-1'));
        setMessage(t('form.error-2'));
        setShowModal(true);
        return;
      }

      let subject = `${formType} Form`;
      let templateId = '';

      if (formType === 'contact') {
        templateId = 'template_0s3ny78'; // Contact form template ID
      } else if (formType === 'volunteer') {
        templateId = 'template_7v2h6z8'; // Volunteer form template ID
      }

      // Email payload (map stable IDs to translated labels)
      const selectedVolunteerLabels = (values.volunteeringTypes || [])
        .map(id => t(`form.options.${id}`));

      const emailValues = {
        ...values,
        volunteeringType: selectedVolunteerLabels.join(", "),
        subject,
        formType,
      };
      await emailjs.send('service_4ya7lnb', templateId, emailValues, '5nz4C0daK6djVDCuk');

      actions.resetForm();
      setTitle(t('form.title-success-1'));
      setMessage(t('form.success-1'));
      setShowModal(true);
    } catch (error) {
      setTitle(t('form.title-error-2'));
      setMessage(t('form.error-3'));
      setShowModal(true);
    }
  };

  const volunteerOptionIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];


  return (
    <>
      {showModal && (
        <CustomModal
          showModal={showModal}
          setShowModal={setShowModal}
          setCaptchaCompleted={setCaptchaExpired}
          title={title}
          message={message}
        />
      )}
      <Formik
        key={i18n.language}  // reinit when language changes so labels update
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          volunteeringTypes: [],  // array of string IDs: "1", "2", ...
          message: '',
          formType: ''
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, submitCount }) => (
          <Form>
            <div className="mb-3">
              <label htmlFor="firstName" className="form-label">{t(firstNameLabel)}</label>
              <Field
                name="firstName"
                type="text"
                className={`form-control`}
                placeholder="John"
                id="firstName"
              />
              <ErrorMessage name="firstName" component="p" className='text-danger' />
            </div>
            <div className="mb-3">
              <label htmlFor="lastName" className="form-label">{t(lastNameLabel)}</label>
              <Field
                name="lastName"
                placeholder="Doe"
                type="text"
                className={`form-control`}
                id="lastName"
              />
              <ErrorMessage name="lastName" component="p" className='text-danger' />
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">{t(emailLabel)}</label>
              <Field
                name="email"
                type="email"
                className={`form-control`}
                id="email"
                placeholder="example@email.com"
              />
              <ErrorMessage name="email" component="p" className='text-danger' />
            </div>
            <div className="mb-3">
              <label htmlFor="phoneNumber" className="form-label">{t(phoneLabel)}</label>
              <Field
                name="phoneNumber"
                type="tel"
                className={`form-control`}
                id="phoneNumber"
                placeholder="+36 123456789"
              />
              <ErrorMessage name="phoneNumber" component="p" className='text-danger' />
            </div>
           
            {formType === 'volunteer' && (
              <div className="mb-3">
                <label className="form-label">{t(typeLabel)}</label>

                <div className="volunteering-options-container">
                  <div className="volunteering-options-list">
                    {volunteerOptionIds.map((id) => {
                      const strId = String(id);
                      return (
                        <div key={strId} className="form-check">
                          <Field
                            type="checkbox"
                            name="volunteeringTypes"
                            value={strId}             
                            className="form-check-input"
                            id={`volunteeringType-${strId}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`volunteeringType-${strId}`}
                          >
                            {t(`form.options.${strId}`)}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(touched.volunteeringTypes || submitCount > 0) && errors.volunteeringTypes && (
                  <p className="text-danger">{errors.volunteeringTypes}</p>
                )}
              </div>
            )}

            {ageLabel && formType !== 'contact' && (
              <div className="mb-3">
                <div className="form-check">
                  <Field
                    className="form-check-input"
                    type="checkbox"
                    id="over18"
                    name="over18"
                  />
                  <label className="form-check-label" htmlFor="over18">
                    {t(ageLabel)}
                  </label>
                </div>
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="message" className="form-label">{t(messageLabel)}</label>
              <Field
                name="message"
                as="textarea"
                placeholder={t("form.message-placeholder")}
                className={`form-control`}
                id="message"
                rows="3"
              />

              <ErrorMessage name="message" component="p" className="text-danger" />
            </div>
            <div className="mb-3 d-flex justify-content-center">
              <div className="recaptcha-wrap">
                <div className="recaptcha-scale">
                  <ReCAPTCHA
                    key={captchaSize}
                    sitekey={process.env.REACT_APP_SITE_KEY}
                    onChange={onChange}
                    onExpired={() => setCaptchaExpired(true)}
                    size={captchaSize}
                    hl={i18n.language}
                  />
                </div>
              </div>
            </div>
            {/* Submit button */}
            <button
              type="submit"
              onClick={(event) => {
                if (captchaExpired) {
                  event.preventDefault(); // Prevent form submission if captcha is expired
                  setTitle(t('form.title-error-1'));
                  setMessage(t('form.error-1'));
                  setShowModal(true);
                }
              }}
              className={`nav__cta m-auto ${isSubmitting || !captchaExpired ? 'disabled' : ''}`}
              style={{width: "100%", opacity: isSubmitting ? 0.5 : 1 }}
            >
              {t(submitLabel)}
            </button>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default CustomForm;
